import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';

export interface SidePanelProps {
  open: boolean;
  /** Called on Escape and on a click on the dimmed backdrop. */
  onClose: () => void;
  /** Id of the element that names the panel (usually its heading). */
  labelledBy?: string;
  /**
   * Ignore Escape while a dialog opened from inside the panel is on top, so one
   * key press closes only the topmost layer.
   */
  suspendDismiss?: boolean;
  className?: string;
  children: React.ReactNode;
}

/**
 * Full-height panel anchored to the right edge, for records you inspect and edit
 * without leaving the list behind. Portaled like `Modal`, and kept one layer
 * below it (z-40) so confirmations opened from the panel stack on top.
 */
export function SidePanel({
  open,
  onClose,
  labelledBy,
  suspendDismiss = false,
  className,
  children,
}: SidePanelProps): React.ReactElement | null {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
      previouslyFocused?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (!open || suspendDismiss) return undefined;
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      // Escape first closes an open dropdown or calendar inside the panel.
      const target = event.target as Element | null;
      if (target?.closest?.('[aria-expanded="true"]')) return;
      if (document.querySelector('[role="listbox"], [data-date-calendar]')) return;
      onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, suspendDismiss, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-40 flex justify-end">
      <div
        className="side-panel-overlay-enter absolute inset-0 bg-gray-950/40 backdrop-blur-[2px]"
        onClick={suspendDismiss ? undefined : onClose}
        aria-hidden
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        className={cn(
          'side-panel-enter relative flex h-full w-full max-w-3xl flex-col bg-white shadow-[-12px_0_40px_-12px_rgba(15,23,42,0.25)] outline-none dark:bg-gray-900',
          className
        )}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}

SidePanel.displayName = 'SidePanel';
